import type { ICampaignWizardForm } from "../../../../../../../../../shared/types/form.type";
import type {
	IChatMessageCampaign,
	IChatMessageCampaignContext,
} from "../../../../../../../../../shared/types/chat.type";

type CampaignContextOverrides = {
	customer_id?: string;
	account_name?: string;
};

const normalizeCustomerId = (value: string | undefined): string | undefined => {
	if (value == null || value === "") return undefined;
	const digits = value.replace(/\D/g, "");
	return digits.length > 0 ? digits : undefined;
};

const buildAdgroups = (
	form: ICampaignWizardForm,
): IChatMessageCampaignContext["adgroups"] => {
	const sourceAdgroups =
		form.campaign_context.adgroups?.length > 0
			? form.campaign_context.adgroups
			: [{ name: "", appads: [] }];

	return sourceAdgroups.map((adgroup) => {
		const nameTrimmed = (adgroup.name ?? "").trim();
		const adgroupObj: IChatMessageCampaignContext["adgroups"][number] = {
			name: nameTrimmed || null,
			appads: [],
		};

		const appads = adgroup.appads ?? [];
		const headlines = appads.flatMap((appad) => appad.headlines ?? []);
		const descriptions = appads.flatMap((appad) => appad.descriptions ?? []);

		if (headlines.length > 0 || descriptions.length > 0) {
			adgroupObj.appads.push({ headlines, descriptions });
		}

		return adgroupObj;
	});
};

const buildCampaignPayload = (
	form: ICampaignWizardForm,
): IChatMessageCampaign | undefined => {
	const raw = form.campaign_form_raw;
	const campaign = form.campaign_context.campaign;
	const payload: IChatMessageCampaign = {};

	const assignScalar = (
		key:
			| "name"
			| "status"
			| "app_store"
			| "app_id"
			| "start_date"
			| "end_date",
		val: unknown,
	): void => {
		if (val === undefined || val === null || val === "") return;
		payload[key] = String(val);
	};

	assignScalar("name", campaign.name);
	assignScalar("status", campaign.status);
	assignScalar("app_store", campaign.app_store);
	assignScalar("app_id", campaign.app_id);

	const targetCpaRaw = String(campaign.target_cpa_micros ?? "").trim();
	if (targetCpaRaw !== "") {
		payload.target_cpa_micros = targetCpaRaw;
	}

	if (Array.isArray(campaign.selective_optimization_conversion_actions)) {
		payload.selective_optimization_conversion_actions =
			campaign.selective_optimization_conversion_actions;
	}
	if (Array.isArray(campaign.geo_targets)) {
		payload.geo_targets = campaign.geo_targets;
	}
	if (Array.isArray(campaign.language_targets)) {
		payload.language_targets = campaign.language_targets;
	}

	assignScalar("start_date", raw.start_date);
	assignScalar("end_date", raw.end_date);

	return Object.keys(payload).length > 0 ? payload : undefined;
};

export const buildNestedCampaignContext = (
	form: ICampaignWizardForm,
	overrides?: CampaignContextOverrides,
): IChatMessageCampaignContext => {
	const campaignCtx = form.campaign_context;
	const result: IChatMessageCampaignContext = {
		adgroups: buildAdgroups(form),
	};

	const customerId =
		normalizeCustomerId(overrides?.customer_id) ??
		normalizeCustomerId(campaignCtx.customer_id);
	if (customerId) {
		result.customer_id = customerId;
	}

	const accountName =
		overrides?.account_name?.trim() || campaignCtx.account_name?.trim() || "";
	if (accountName) {
		result.account_name = accountName;
	}

	const budgetAmountRaw = String(campaignCtx.budget?.amount_micros ?? "").trim();
	if (budgetAmountRaw !== "") {
		result.budget = { amount_micros: budgetAmountRaw };
	}

	const campaignPayload = buildCampaignPayload(form);
	if (campaignPayload) {
		result.campaign = campaignPayload;
	}

	return result;
};
